import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import SearchResults from "_shared/_api/search/search-results";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import MarketplaceForumSearchAPIRequest from "marketplace/_api/queries/marketplace-forum-search-api-query";
import MarketplaceSearchAPIRequest from "marketplace/_api/queries/marketplace-search-api-query";
import ForumSchema, { ForumParams } from "messaging/forums/_schemas/forum-schema";
import Forum from "messaging/forums/forum";
import Session from "users/__session/session";
import MarketplaceSearchInfoAPIResponse from "./response-contracts/marketplace-search-info-api-response";

export default class MarketplaceAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create MarketplaceAPIService without session.");
    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async searchAll(
    search: MarketplaceSearchAPIRequest,
    abortController: AbortController,
  ): Promise<SearchResults<MarketplaceSearchInfoAPIResponse>> {
    let params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
      if (value) {
        params.append(key, value.toString());
      }
    }
    const url = new URL(`/marketplace/all?${params.toString()}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return new SearchResults<MarketplaceSearchInfoAPIResponse>(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new MarketplaceSearchError(search, error);
    }
  }

  async getMarketplaceForumsAsync(
    search: MarketplaceForumSearchAPIRequest[],
    abortController: AbortController,
  ): Promise<Forum[]> {
    const url = new URL(`/marketplace/forums/search`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), {
        headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
        signal: abortController?.signal,
        data: search,
      });

      const forumParamsArray = ForumSchema.deserializeJSONArray(response.data);
      return forumParamsArray.map((params: ForumParams) => new Forum(params));
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (!axios.isAxiosError(error)) throw error;
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
        case HttpStatusCode.Forbidden:
          throw new Error(`Unable to get marketplace forums. ${error.response.data}`);
        default:
          throw error;
      }
    }
  }
}

export class MarketplaceSearchError extends Error {
  search: MarketplaceSearchAPIRequest;
  error: unknown;
  constructor(search: MarketplaceSearchAPIRequest, error: unknown) {
    super(`Unable to search marketplace with ${JSON.stringify(search)}.`);
    this.search = search;
    this.error = error;
  }
}
